
export interface NetworkSegment {
    url: string;
    cdn: 'Akamai' | 'Fastly' | 'G-Core' | 'AWS' | 'Telenet' | 'o|i' | 'USP' | 'Unknown' | string;
    node: 'Origin' | 'Shield' | 'Edge' | 'ISP' | 'Unknown';
    location: string;
    ttfb: number;
    throughput?: number; // bytes per second
    timestamp: number;
    // Optional Header Data for refined topology
    xAmzCfPop?: string;
    xIdShield?: string;
    xServedBy?: string;
}

type AnalysisCallback = (segment: NetworkSegment) => void;
import { SmartGeoResolver } from './geo/SmartGeoResolver';
import { createLogger } from '../utils/logger';
const log = createLogger('DeepPacketAnalyser');


/**
 * Normalized headers interface for CDN detection
 * Used by both browser (analyze) and Electron (analyzeFromElectron) paths
 */
export interface ParsedHeaders {
    server: string | null;
    xCDN: string | null;
    xAmzCfPop: string | null;
    xAmzCfId?: string | null;
    via?: string | null;
    xCache?: string | null;
    xId: string | null;
    xIdShield: string | null;
    xServedBy: string | null;
    xUsp?: string | null;
    /** True if any header name contains "akamai" (e.g. Akamai-Mon-iucid-Del) */
    hasAkamaiHeader?: boolean;
}

/** Result of CDN detection */
interface CdnDetectionResult {
    cdn: NetworkSegment['cdn'] | null;
    node: NetworkSegment['node'] | null;
    location: string | null;
}



export class DeepPacketAnalyser {
    private static instance: DeepPacketAnalyser;
    private listeners: Set<AnalysisCallback> = new Set();

    // Smart Caching & De-duplication
    private cache: Map<string, {
        cdn: NetworkSegment['cdn'];
        node: NetworkSegment['node'];
        location: string;
        allowedHeaders?: Set<string>; // allowedHeaders is optional as it might not be set initially
    }> = new Map();
    private pendingResolutions: Map<string, Promise<void>> = new Map();

    private constructor() {
        // Now passive - waiting for Player Adapters to call analyze()
    }

    public static getInstance(): DeepPacketAnalyser {
        if (!DeepPacketAnalyser.instance) {
            DeepPacketAnalyser.instance = new DeepPacketAnalyser();
        }
        return DeepPacketAnalyser.instance;
    }

    public subscribe(callback: AnalysisCallback): () => void {
        this.listeners.add(callback);
        return () => {
            this.listeners.delete(callback);
        };
    }

    private notify(segment: NetworkSegment) {
        this.listeners.forEach(cb => cb(segment));
    }

    public reset(): void {
        this.cache.clear(); // Clear the cache map
        this.pendingResolutions.clear();
    }

    /**
     * Shared CDN detection logic
     * Used by both analyze() and analyzeFromElectron()
     * 
     * @param headers - Normalized headers object
     * @param url - Request URL (for fallback domain-based detection)
     * @returns CdnDetectionResult with cdn, node, and location
     */
    private detectCdn(headers: ParsedHeaders, url: string): CdnDetectionResult {
        let cdn: NetworkSegment['cdn'] | null = null;
        let node: NetworkSegment['node'] | null = null;
        let location: string | null = null;

        // 1. Explicit X-CDN header has highest priority
        if (headers.xCDN) {
            const xCdn = headers.xCDN.toLowerCase();

            if (xCdn === 'oli') {
                cdn = 'o|i';
                node = 'Edge';
            } else if (xCdn === 'gcore') {
                cdn = 'G-Core';
                node = 'Edge';
            } else if (xCdn === 'akamai') {
                cdn = 'Akamai';
                node = 'Edge';
            } else if (xCdn === 'fastly') {
                cdn = 'Fastly';
                node = 'Edge';
                if (headers.xServedBy) {
                    location = this.parseLocationFromServedBy(headers.xServedBy);
                }
            } else {
                // Fallback: Use the x-cdn header value capitalize
                cdn = (headers.xCDN.charAt(0).toUpperCase() + headers.xCDN.slice(1)) as NetworkSegment['cdn'];
                node = 'Edge';
                log.log(`Generic CDN detected from x-cdn: ${headers.xCDN}`);
            }
        }

        // 2. Server header / heuristics (if not determined by X-CDN)
        if (!cdn) {
            if (headers.server === 'AkamaiGHost') {
                cdn = 'Akamai';
                node = 'Edge';
            } else if ((headers.server?.includes('Varnish') || headers.via?.toLowerCase().includes('varnish')) && headers.xServedBy) {
                cdn = 'Fastly';
                node = 'Edge';
                location = this.parseLocationFromServedBy(headers.xServedBy);
            } else if (headers.server?.toLowerCase().includes('akamai') || headers.hasAkamaiHeader) {
                cdn = 'Akamai';
                node = 'Edge';
            } else if (headers.xAmzCfPop) {
                cdn = 'AWS';
                node = 'Edge';
                location = this.parseLocationFromCfPop(headers.xAmzCfPop);
            } else if (url.includes('telenet-ops.be')) {
                cdn = 'Telenet';
                node = 'Edge';
            }
        }

        return { cdn, node, location };
    }


    /**
     * Analyze headers received from Electron main process (bypasses CORS!)
     * This method receives ALL headers captured by session.webRequest.
     * 
     * @param url - The request URL
     * @param headers - Flattened headers object (lowercase keys)
     * @param ttfb - Time to first byte in milliseconds  
     * @param timestamp - Request timestamp
     * @param requestType - 'Manifest' or 'Segment'
     */
    public analyzeFromElectron(
        url: string,
        headers: Record<string, string>,
        ttfb: number,
        timestamp: number,
        requestType: 'Manifest' | 'Segment'
    ): void {
        const urlObj = new URL(url);
        const host = urlObj.hostname;

        // For segments, use cache if available
        if (requestType === 'Segment') {
            const cached = this.cache.get(host);
            if (cached) {
                const segment: NetworkSegment = {
                    url,
                    cdn: cached.cdn,
                    node: cached.node,
                    location: cached.location,
                    ttfb,
                    timestamp
                };
                this.notify(segment);
                return;
            }
        }

        // Parse headers (already lowercase from main process)
        const hasAkamaiHeader = Object.keys(headers).some(k => k.includes('akamai'));
        const parsedHeaders: ParsedHeaders = {
            server: headers['server'] || null,
            xCDN: headers['x-cdn'] || null,
            xAmzCfPop: headers['x-amz-cf-pop'] || null,
            xAmzCfId: headers['x-amz-cf-id'] || null,
            via: headers['via'] || null,
            xCache: headers['x-cache'] || null,
            xId: headers['x-id'] || null,
            xIdShield: headers['x-id-shield'] || null,
            xServedBy: headers['x-served-by'] || null,
            xUsp: headers['x-usp'] || null,
            hasAkamaiHeader,
        };

        // Use shared CDN detection logic
        const { cdn, node, location } = this.detectCdn(parsedHeaders, url);

        // Debug log extended headers (single logging point for Electron path)
        if (parsedHeaders.xAmzCfPop || parsedHeaders.xIdShield || parsedHeaders.xId) {
            log.log(`Extended Headers for ${host}:`, {
                cdn,
                location,
                xAmzCfPop: parsedHeaders.xAmzCfPop,
                xIdShield: parsedHeaders.xIdShield,
                xId: parsedHeaders.xId,
                xUsp: parsedHeaders.xUsp
            });
        }

        // Update cache
        const cachedInfo = this.cache.get(host);
        const isFirstDetection = !cachedInfo;
        const cdnChanged = cachedInfo && cdn && cdn !== 'Unknown' && cdn !== cachedInfo.cdn;

        if (cdnChanged) {
            log.log(`CDN Switch: ${cachedInfo?.cdn} -> ${cdn}`);
        }

        this.cache.set(host, { 
            cdn: cdn || 'Unknown', 
            node: node || 'Unknown', 
            location: location || 'Unknown' 
        });

        const segment: NetworkSegment = {
            url,
            cdn: cdn || 'Unknown',
            node: node || 'Unknown',
            location: location || 'Unknown',
            ttfb,
            timestamp,
            xAmzCfPop: parsedHeaders.xAmzCfPop || undefined,
            xIdShield: parsedHeaders.xIdShield || undefined
        };

        // Notify on first detection or CDN change
        if (isFirstDetection || cdnChanged) {
            log.log(`Electron detected CDN: ${cdn || 'Unknown'} at ${location || 'Unknown'} for ${host}`);
            this.notify(segment);
        }
    }

    /**
     * Parse location from Fastly X-Served-By header
     */
    private parseLocationFromServedBy(servedBy: string): string | null {
        const match = servedBy.match(/cache-([a-z]{3})\d+-([A-Z]{3})/i);
        if (match) {
            const locationCode = match[2];
            const resolved = SmartGeoResolver.lookupLocationCode(locationCode);
            if (resolved) return resolved.city;
        }

        // Fallback to simple inclusion check if regex fails
        const s = servedBy.toLowerCase();
        if (s.includes('bru')) return 'Brussels';
        if (s.includes('ams')) return 'Amsterdam';
        if (s.includes('fra')) return 'Frankfurt';
        if (s.includes('par') || s.includes('cdg')) return 'Paris';
        if (s.includes('lhr') || s.includes('lon')) return 'London';

        return null;
    }

    /**
     * Parse location from CloudFront X-Amz-Cf-Pop header
     */
    private parseLocationFromCfPop(pop: string): string | null {
        // Try exact code lookup first
        const code = pop.substring(0, 3);
        const resolved = SmartGeoResolver.lookupLocationCode(code);
        if (resolved) return resolved.city;

        // Fallback / legacy checks
        const p = pop.toUpperCase();
        if (p.startsWith('AMS')) return 'Amsterdam';
        if (p.startsWith('FRA')) return 'Frankfurt';
        if (p.startsWith('CDG') || p.startsWith('PAR')) return 'Paris';
        if (p.startsWith('LHR') || p.startsWith('LON')) return 'London';
        if (p.startsWith('BRU')) return 'Brussels';

        return null;
    }

    /**
     * Analyze a network request (called by Player Adapters)
     */
    public async analyze(
        url: string,
        xhr: XMLHttpRequest,
        ttfb: number,
        timestamp: number,
        requestType: 'Manifest' | 'Segment',
        throughput?: number
    ) {
        const urlObj = new URL(url);
        const host = urlObj.hostname;


        // --- SCOPE 1: SEGMENT HANDLING (Metrics Only) ---
        // If it's a segment, we trust the cache
        if (requestType === 'Segment') {
            const cached = this.cache.get(host);
            if (cached) {
                // REUSE CACHE - High Frequency Updates for Graphs
                const segment: NetworkSegment = {
                    url: url,
                    cdn: cached.cdn,
                    node: cached.node,
                    location: cached.location,
                    ttfb: ttfb,
                    throughput: throughput,
                    timestamp: timestamp
                };
                this.notify(segment);
                return;
            }
            // If no cache exists yet (edge case: missed manifest), we might fall through
            // to full analysis OR just return Unknown.
            // Let's fall through to attempt detection if cache is missing.
        }

        // --- SCOPE 2: MANIFEST HANDLING (Topology & Identity) ---
        // Full Header Analysis, DNS, Geolocation
        // This is "heavy" but happens less frequently.

        // De-duplication: If analysis is already in progress for this host, wait for it.
        if (this.pendingResolutions.has(host)) {
            // console.log(`[DeepPacketAnalyser] Analysis already pending for ${host}, waiting...`);
            await this.pendingResolutions.get(host);
            // After waiting, check cache again. If successfully resolved, use it.
            const cached = this.cache.get(host);
            if (cached) {
                const segment: NetworkSegment = {
                    url: url,
                    cdn: cached.cdn,
                    node: cached.node,
                    location: cached.location,
                    ttfb: ttfb,
                    throughput: throughput,
                    timestamp: timestamp
                };
                this.notify(segment);
                return;
            }
        }

        // Start new analysis
        let resolveAnalysis: () => void;
        const analysisPromise = new Promise<void>((resolve) => { resolveAnalysis = resolve; });
        this.pendingResolutions.set(host, analysisPromise);

        try {
            // NO MATCH or FIRST RUN: Parse Headers

            // 1. Dynamic Header Discovery (FIX: Use getAllResponseHeaders() to know what is exposed)
            let allowedHeaders = new Set<string>();

            const cached = this.cache.get(host);
            if (cached && cached.allowedHeaders) {
                allowedHeaders = cached.allowedHeaders;
            } else {
                // SAFER DISCOVERY: Parse getAllResponseHeaders() instead of asking for Access-Control-Expose-Headers directly
                // This prevents "Refused to get unsafe header" warning because getAllResponseHeaders() is always safe.
                const allHeaders = xhr.getAllResponseHeaders();
                if (allHeaders) {
                    const lines = allHeaders.trim().split(/[\r\n]+/);
                    lines.forEach(line => {
                        const parts = line.split(':');
                        if (parts.length > 0) {
                            allowedHeaders.add(parts[0].trim().toLowerCase());
                        }
                    });
                }
            }

            // Helper to safely get header
            const getSafeHeader = (name: string): string | null => {
                const lowerName = name.toLowerCase();
                // Check if we SAW this header exposed in getAllResponseHeaders
                if (allowedHeaders.size > 0 && !allowedHeaders.has(lowerName)) {
                    return null;
                }
                // Double check: catch any refusal just in case
                try {
                    return xhr.getResponseHeader(name);
                } catch (e) {
                    // If browser still complains, suppress it (though getAllResponseHeaders check should prevent this)
                    return null;
                }
            };

            // 1. IDENTITY: Parse Headers via getSafeHeader to avoid warnings
            const hasAkamaiHeader = [...allowedHeaders].some(h => h.includes('akamai'));
            const parsedHeaders: ParsedHeaders = {
                server: getSafeHeader('Server'),
                xCDN: getSafeHeader('X-CDN'),
                xAmzCfPop: getSafeHeader('X-Amz-Cf-Pop'),
                xId: getSafeHeader('X-ID'),
                xIdShield: getSafeHeader('X-ID-Shield'),
                xServedBy: getSafeHeader('X-Served-By'),
                hasAkamaiHeader,
            };

            // Use shared CDN detection logic
            let { cdn, node, location: detectedLocation } = this.detectCdn(parsedHeaders, url);
            let location = detectedLocation;

            // CACHE & CDN CHANGE DETECTION:
            // Track CDN changes to trigger topology updates.
            const cachedInfo = this.cache.get(host);
            const isFirstDetection = !cachedInfo;
            let cdnChanged = false;

            if (cachedInfo) {
                if (cdn && cdn !== 'Unknown' && cdn !== cachedInfo.cdn) {
                    // CDN SWITCH DETECTED - Log and mark for notification
                    log.log(`CDN Switch Detected: ${cachedInfo.cdn} -> ${cdn}`);
                    cdnChanged = true;
                    // Keep new CDN, don't reuse cache since CDN changed
                } else {
                    // Reuse cached info (same CDN or headers say Unknown)
                    location = cachedInfo.location;
                    if (!cdn || cdn === 'Unknown') cdn = cachedInfo.cdn;
                    node = cachedInfo.node;
                }
            }

            // Log first detection for this host (always log, even if Unknown for debug)
            if (isFirstDetection) {
                log.log(`CDN detected for ${host}: ${cdn || 'Unknown'} (X-CDN: ${parsedHeaders.xCDN || 'none'}, Server: ${parsedHeaders.server || 'none'})`);
            }

            // Update Cache
            this.cache.set(host, {
                cdn: cdn || 'Unknown',
                node: node || 'Unknown',
                location: location || 'Unknown',
                allowedHeaders: allowedHeaders
            });

            const segment: NetworkSegment = {
                url: url,
                cdn: cdn || 'Unknown',
                node: node || 'Unknown',
                location: location || 'Unknown',
                ttfb: ttfb,
                throughput: throughput,
                timestamp: timestamp,
                // Pass extra metadata if available
                xAmzCfPop: parsedHeaders.xAmzCfPop || undefined,
                xIdShield: parsedHeaders.xIdShield || undefined,
                xServedBy: parsedHeaders.xServedBy || undefined
            };

            // Notify listeners on first detection or CDN change
            // Always report (even Unknown) - topology logic handles visualization
            if (isFirstDetection || cdnChanged) {
                this.notify(segment);
            }
        } finally {
            // Signal completion to blocked requests
            resolveAnalysis!();
            this.pendingResolutions.delete(host);
        }
    }
}
